/* Jullien Stellar | Email campaign gift | Shopify native cart; no discount code required. */
(() => {
  'use strict';
  const node = document.getElementById('email-campaign-gift-config');
  if (!node) return;
  let data;
  try { data = JSON.parse(node.textContent); } catch (error) {
    console.error('[JS Gift] Invalid theme configuration', error); return;
  }
  const campaign = String(data.campaign || '').trim();
  const giftVariantId = Number(data.giftVariantId || 0);
  const productIds = new Set((data.eligibleProductIds || []).map(Number));
  const variantIds = new Set(String(data.eligibleVariantIds || '').split(/[\s,;]+/).map(Number).filter(Boolean));
  const root = String(data.root || '/').replace(/\/?$/, '/');
  const storageKey = 'jsb_campaign_gift_' + campaign;
  const giftProperty = '_Email campaign gift';
  const attribute = 'Email campaign';
  let enrolled = new URLSearchParams(location.search).get('promo') === campaign;
  try {
    if (enrolled) sessionStorage.setItem(storageKey, '1');
    enrolled = enrolled || sessionStorage.getItem(storageKey) === '1';
  } catch (error) { console.warn('[JS Gift] Session storage unavailable', error); }

  // Expose status for quick support diagnosis, without changing storefront UI.
  const status = { enrolled, campaign, giftVariantId, productIds: [...productIds], variantIds: [...variantIds], lastError: null, lastCart: null };
  window.JullienGiftStatus = status;
  if (!campaign || !giftVariantId || (!productIds.size && !variantIds.size)) {
    status.lastError = 'Missing campaign, gift variant ID, or qualifying products. For an Unlisted gift, enter its numeric variant ID in Theme settings > Email campaign free gift.';
    console.warn('[JS Gift]', status.lastError, status);
    return;
  }
  if (!enrolled) return;

  const originalFetch = window.fetch.bind(window);
  let running = false;
  let queued = false;
  let debounce;
  const cartUrl = (operation = '') => root + 'cart' + (operation ? '/' + operation : '') + '.js';
  const api = async (operation, body) => {
    const response = await originalFetch(cartUrl(operation), body ? {
      method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
    } : { credentials: 'same-origin' });
    const responseBody = await response.json();
    if (!response.ok) throw new Error(responseBody.description || responseBody.message || `HTTP ${response.status}`);
    return responseBody;
  };
  const isEligible = item => Number(item.variant_id || item.id) !== giftVariantId &&
    (variantIds.size ? variantIds.has(Number(item.variant_id || item.id)) : productIds.has(Number(item.product_id)));
  const isOurGift = item => Number(item.variant_id || item.id) === giftVariantId &&
    item.properties && item.properties[giftProperty] === campaign;

  // Shopify's section-rendering response is the source of truth. Re-render only
  // the drawer content and cart badge, preserving the existing open drawer.
  function paintCartSections(sections) {
    if (!sections || !sections['cart-drawer']) return false;
    const drawer = document.querySelector('cart-drawer');
    const parser = new DOMParser();
    if (drawer) {
      const latest = parser.parseFromString(sections['cart-drawer'], 'text/html');
      const fresh = latest.querySelector('#CartDrawer');
      const current = drawer.querySelector('#CartDrawer');
      if (!fresh || !current) return false;
      // Replaces Dawn's inner drawer in one operation, including item counters,
      // totals, quantity controls and empty-cart state.
      current.innerHTML = fresh.innerHTML;
      drawer.classList.toggle('is-empty', !current.querySelector('.cart-item'));
      const overlay = current.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', drawer.close.bind(drawer));
    }
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && sections['cart-icon-bubble']) {
      const freshBubble = parser.parseFromString(sections['cart-icon-bubble'], 'text/html').querySelector('#cart-icon-bubble');
      if (freshBubble) bubble.innerHTML = freshBubble.innerHTML;
    }
    return true;
  }

  async function refreshCart() {
    if (location.pathname.replace(/\/$/, '') === (root + 'cart').replace(/\/$/, '')) {
      location.reload(); return;
    }
    try {
      // The cart section endpoint provides fresh server-rendered HTML. The
      // timestamp prevents stale section HTML from being reused by caches.
      const url = root + 'cart?sections=cart-drawer,cart-icon-bubble&_=' + Date.now();
      const response = await originalFetch(url, { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error('Cart sections returned HTTP ' + response.status);
      const sections = await response.json();
      if (!paintCartSections(sections)) throw new Error('No valid cart drawer section returned');
    } catch (error) {
      status.lastError = 'Cart drawer display: ' + String(error.message || error);
      console.warn('[JS Gift] Drawer did not refresh; checkout cart remains correct:', error);
    }
  }

  async function sync() {
    if (running) { queued = true; return; }
    running = true;
    let cartChanged = false;
    try {
      const cart = await api('');
      status.lastCart = { items: cart.items.map(i => ({ product_id: i.product_id, variant_id: i.variant_id, quantity: i.quantity, properties: i.properties })) };
      const eligible = cart.items.some(isEligible);
      const gifts = cart.items.filter(isOurGift);
      if (eligible && !gifts.length) {
        const added = await api('add', {
          items: [{ id: giftVariantId, quantity: 1, properties: { [giftProperty]: campaign } }],
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: location.pathname
        });
        cartChanged = true;
        if (added.sections) paintCartSections(added.sections);
      } else if (gifts.length && (!eligible || gifts.length > 1 || gifts[0].quantity !== 1)) {
        for (let i = 0; i < gifts.length; i++) {
          await api('change', { id: gifts[i].key, quantity: eligible && i === 0 ? 1 : 0 });
        }
        cartChanged = true;
      }
      const existingAttribute = cart.attributes?.[attribute] || '';
      if (eligible && existingAttribute !== campaign) await api('update', { attributes: { [attribute]: campaign } });
      else if (!eligible && existingAttribute === campaign) await api('update', { attributes: { [attribute]: '' } });
      status.lastError = null;
      if (cartChanged) {
        // Dawn may still be painting its own add-to-cart response. Run the
        // final refresh after that completes, so the gift remains visible.
        await new Promise(resolve => setTimeout(resolve, 450));
        await refreshCart();
      }
    } catch (error) {
      status.lastError = String(error.message || error);
      console.error('[JS Gift] Could not add or synchronize gift:', error, status);
    } finally {
      running = false;
      if (queued) { queued = false; schedule(); }
    }
  }
  function schedule() { clearTimeout(debounce); debounce = setTimeout(sync, 350); }
  // Dawn product forms, cart drawer and cart page all use fetch; schedule after those edits.
  window.fetch = function(input, init) {
    const result = originalFetch(input, init);
    let pathname = '';
    try { pathname = new URL(typeof input === 'string' ? input : input.url, location.href).pathname; } catch (_) { /* Unknown URL */ }
    if (/\/cart\/(add|change|update|clear)(?:\.js)?\/?$/.test(pathname)) {
      result.then(response => { if (response.ok) schedule(); }).catch(() => {});
    }
    return result;
  };
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, schedule);
  }
  document.addEventListener('visibilitychange', () => { if (!document.hidden) schedule(); });
  window.addEventListener('pageshow', schedule);
  schedule();
})();